/**
 * Shared option lists for settings select / multi-select controls.
 *
 * Enum option values mirror the OpenAPI contract enums exactly; labels are the
 * human-readable presentation. Locale / currency / timezone lists are a curated
 * starting set (the controls still accept whatever the server returns).
 */
import type {
  AddressFormat,
  AiAgentType,
  AiAutonomyMode,
  CartMergeStrategy,
  DeliveryModel,
  EngineMode,
  InventoryTrackingMode,
  NotificationChannel,
  OmsMode,
  OutOfStockBehavior,
  PaymentCaptureMode,
  PaymentMethod,
  RegistrationMode,
  ReservationPolicy,
  SalesMode,
  StoreDayOfWeek,
  StoreLocationServiceType,
  StoreLocationType,
  StoreRegionType,
} from '../model/settingsTypes';

export interface Option<T extends string = string> {
  value: T;
  label: string;
}

export const LANGUAGE_OPTIONS: Option[] = [
  { value: 'ru-RU', label: 'Русский (ru-RU)' },
  { value: 'en-US', label: 'English (en-US)' },
  { value: 'en-GB', label: 'English (en-GB)' },
  { value: 'kk-KZ', label: 'Қазақша (kk-KZ)' },
  { value: 'uz-UZ', label: 'O‘zbek (uz-UZ)' },
];

/**
 * Bare locale codes for the Content domain's `defaultLocale` (web-content-service page
 * lookup). NOT the same vocabulary as `LANGUAGE_OPTIONS` (BCP-47 tags for
 * `BusinessInformation.locale`) — content pages can only be published with `locale ∈
 * {'ru', 'en'}` (mirrors `ContentPagesPage.tsx`'s `LOCALE_OPTIONS`). Picking a BCP-47
 * tag here would not match any published page's locale and would blank the storefront's
 * "About shop" content silently — do not reuse `LANGUAGE_OPTIONS` for this field.
 */
export const CONTENT_LOCALE_OPTIONS: Option[] = [
  { value: 'ru', label: 'Russian (ru)' },
  { value: 'en', label: 'English (en)' },
];

export const CURRENCY_OPTIONS: Option[] = [
  { value: 'RUB', label: 'Russian Ruble (RUB)' },
  { value: 'USD', label: 'US Dollar (USD)' },
  { value: 'EUR', label: 'Euro (EUR)' },
  { value: 'KZT', label: 'Kazakhstani Tenge (KZT)' },
  { value: 'UZS', label: 'Uzbekistani Som (UZS)' },
];

export const TIMEZONE_OPTIONS: Option[] = [
  { value: 'Europe/Kaliningrad', label: 'Europe/Kaliningrad (UTC+2)' },
  { value: 'Europe/Moscow', label: 'Europe/Moscow (UTC+3)' },
  { value: 'Europe/Samara', label: 'Europe/Samara (UTC+4)' },
  { value: 'Asia/Yekaterinburg', label: 'Asia/Yekaterinburg (UTC+5)' },
  { value: 'Asia/Almaty', label: 'Asia/Almaty (UTC+6)' },
  { value: 'Asia/Krasnoyarsk', label: 'Asia/Krasnoyarsk (UTC+7)' },
  { value: 'Asia/Vladivostok', label: 'Asia/Vladivostok (UTC+10)' },
];

export const DELIVERY_MODEL_OPTIONS: Option<DeliveryModel>[] = [
  { value: 'COURIER', label: 'Courier delivery' },
  { value: 'PICKUP_FROM_STORE', label: 'Pickup from store' },
  { value: 'PICKUP_POINT', label: 'Pickup point' },
  { value: 'CLICK_AND_COLLECT', label: 'Click & Collect' },
  { value: 'CLICK_AND_RESERVE', label: 'Click & Reserve' },
  { value: 'SAME_DAY', label: 'Same-day delivery' },
];

export const PAYMENT_METHOD_OPTIONS: Option<PaymentMethod>[] = [
  { value: 'CARD_ONLINE', label: 'Online card payment' },
  { value: 'CASH_ON_DELIVERY', label: 'Cash on delivery' },
  { value: 'INSTALLMENTS', label: 'Installments' },
  { value: 'CREDIT', label: 'Credit' },
];

export const PAYMENT_CAPTURE_MODE_OPTIONS: Option<PaymentCaptureMode>[] = [
  { value: 'ONE_STAGE', label: 'One-stage (capture immediately)' },
  { value: 'TWO_STAGE', label: 'Two-stage (authorise, then capture)' },
];

export const OMS_MODE_OPTIONS: Option<OmsMode>[] = [
  { value: 'INTERNAL', label: 'Internal OMS' },
  { value: 'EXTERNAL', label: 'External OMS' },
];

export const ENGINE_MODE_OPTIONS: Option<EngineMode>[] = [
  { value: 'INTERNAL', label: 'Internal engine' },
  { value: 'EXTERNAL', label: 'External engine' },
];

export const REGISTRATION_MODE_OPTIONS: Option<RegistrationMode>[] = [
  { value: 'OPEN', label: 'Open registration' },
  { value: 'INVITE_ONLY', label: 'Invite only' },
  { value: 'DISABLED', label: 'Disabled' },
];

export const NOTIFICATION_CHANNEL_OPTIONS: Option<NotificationChannel>[] = [
  { value: 'EMAIL', label: 'Email' },
  { value: 'SMS', label: 'SMS' },
  { value: 'PUSH', label: 'Push' },
  { value: 'TELEGRAM', label: 'Telegram' },
];

// ── Platform services added in docs/obzr-ecom-platform.md ────────────────────────

export const SALES_MODE_OPTIONS: Option<SalesMode>[] = [
  { value: 'PRE_OPENING', label: 'Pre-opening — preview only' },
  { value: 'ACTIVE', label: 'Active — storefront live' },
  { value: 'PAUSED', label: 'Paused — browsing, no orders' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
];

export const STORE_LOCATION_TYPE_OPTIONS: Option<StoreLocationType>[] = [
  { value: 'RETAIL', label: 'Retail store' },
  { value: 'WAREHOUSE', label: 'Warehouse' },
  { value: 'PICKUP_POINT', label: 'Pickup point' },
  { value: 'DARK_STORE', label: 'Dark store' },
];

export const STORE_SERVICE_LOCATION_TYPE_OPTIONS: Option<StoreLocationServiceType>[] = [
  { value: 'STORE', label: 'Store' },
  { value: 'WAREHOUSE', label: 'Warehouse' },
  { value: 'DARK_STORE', label: 'Dark store' },
  { value: 'SHOWROOM', label: 'Showroom' },
  { value: 'PICKUP_POINT', label: 'Pickup point' },
];

export const STORE_REGION_TYPE_OPTIONS: Option<StoreRegionType>[] = [
  { value: 'COUNTRY', label: 'Country' },
  { value: 'REGION', label: 'Region' },
  { value: 'CITY', label: 'City' },
];

/**
 * Monday-first day-of-week options for the store-service `StoreBusinessHours` editor
 * (`LocationDialog`'s business-hours grid). NOT the same constant as {@link WEEKDAYS}
 * below, which drives a different, legacy numeric-day `WorkingHours` type — do not
 * conflate the two.
 */
export const STORE_DAY_OF_WEEK_OPTIONS: Option<StoreDayOfWeek>[] = [
  { value: 'MONDAY', label: 'Monday' },
  { value: 'TUESDAY', label: 'Tuesday' },
  { value: 'WEDNESDAY', label: 'Wednesday' },
  { value: 'THURSDAY', label: 'Thursday' },
  { value: 'FRIDAY', label: 'Friday' },
  { value: 'SATURDAY', label: 'Saturday' },
  { value: 'SUNDAY', label: 'Sunday' },
];

export const ADDRESS_FORMAT_OPTIONS: Option<AddressFormat>[] = [
  { value: 'STRUCTURED', label: 'Structured (separate fields)' },
  { value: 'FREEFORM', label: 'Free-form (single line)' },
];

export const INVENTORY_TRACKING_MODE_OPTIONS: Option<InventoryTrackingMode>[] = [
  { value: 'DISABLED', label: 'Disabled (no stock tracking)' },
  { value: 'TRACK_QUANTITY', label: 'Track quantity' },
  { value: 'TRACK_BATCH', label: 'Track by batch / lot' },
];

export const RESERVATION_POLICY_OPTIONS: Option<ReservationPolicy>[] = [
  { value: 'NONE', label: 'No reservation' },
  { value: 'ON_CHECKOUT', label: 'Reserve at checkout' },
  { value: 'ON_PAYMENT', label: 'Reserve on payment' },
];

export const OUT_OF_STOCK_BEHAVIOR_OPTIONS: Option<OutOfStockBehavior>[] = [
  { value: 'HIDE', label: 'Hide the product' },
  { value: 'SHOW_DISABLED', label: 'Show, not purchasable' },
  { value: 'SHOW_BACKORDER', label: 'Show, allow backorder' },
];

export const AI_AGENT_TYPE_OPTIONS: Option<AiAgentType>[] = [
  { value: 'SUPPORT', label: 'Support assistant' },
  { value: 'ORDER_PROCESSING', label: 'Order processing' },
  { value: 'PRICE_PROMO_ASSISTANT', label: 'Price & promo assistant' },
  { value: 'CATALOG_MANAGER', label: 'Catalog manager' },
];

export const AI_AUTONOMY_MODE_OPTIONS: Option<AiAutonomyMode>[] = [
  { value: 'SUGGEST', label: 'Suggest only (human approves)' },
  { value: 'SEMI_AUTONOMOUS', label: 'Semi-autonomous' },
  { value: 'AUTONOMOUS', label: 'Autonomous' },
];

export const CART_MERGE_STRATEGY_OPTIONS: Option<CartMergeStrategy>[] = [
  { value: 'MERGE', label: 'Merge guest + account carts' },
  { value: 'REPLACE', label: 'Replace with account cart' },
  { value: 'KEEP_GUEST', label: 'Keep the guest cart' },
];

// ── Tenant capabilities, sales-mode help & flag helpers ──────────────────────────

/** A well-known tenant storefront capability surfaced as a first-class toggle. */
export interface CapabilityFlag {
  /** The canonical tenant settings field key (mirrors web-tenant-settings-service FeatureFlags). */
  key: string;
  label: string;
  description: string;
}

/**
 * The platform storefront purchasing funnel, exposed as first-class capability toggles on the Tenant
 * settings page. Keys mirror web-tenant-settings-service `FeatureFlags`
 * (`catalogEnabled` … `paymentEnabled`).
 */
export const TENANT_CAPABILITY_FLAGS: CapabilityFlag[] = [
  { key: 'catalogEnabled', label: 'Catalog browsing', description: 'Customers can browse products and categories.' },
  { key: 'basketEnabled', label: 'Shopping basket', description: 'Customers can add products to a cart.' },
  { key: 'checkoutEnabled', label: 'Checkout', description: 'Customers can start the checkout flow.' },
  { key: 'ordersEnabled', label: 'Orders', description: 'Customers can place and track orders.' },
  { key: 'paymentEnabled', label: 'Online payment', description: 'Online payment is available at checkout.' },
];

/** What each storefront sales mode does — shown as contextual help under the selector. */
export const SALES_MODE_HELP: Record<SalesMode, string> = {
  PRE_OPENING: 'Preview only — the storefront is visible but customers cannot order yet.',
  ACTIVE: 'Storefront is fully live — browsing and ordering are available.',
  PAUSED: 'Customers can browse, but checkout and ordering are paused.',
  MAINTENANCE: 'Storefront is closed for maintenance.',
};

/** Day-of-week labels for working-hours editors (1 = Monday … 7 = Sunday). */
export const WEEKDAYS: { day: number; label: string }[] = [
  { day: 1, label: 'Monday' },
  { day: 2, label: 'Tuesday' },
  { day: 3, label: 'Wednesday' },
  { day: 4, label: 'Thursday' },
  { day: 5, label: 'Friday' },
  { day: 6, label: 'Saturday' },
  { day: 7, label: 'Sunday' },
];

/** Human-readable label for an arbitrary feature-flag key (e.g. `loyalty_program` → "Loyalty program"). */
export function humanizeFlagKey(key: string): string {
  const words = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\benabled\b/gi, '')
    .trim();
  if (!words) {
    return key;
  }
  return words.charAt(0).toUpperCase() + words.slice(1);
}
